import { useContext, useMemo, useState, useEffect, useRef } from 'react';
import { PageContext } from 'src/context/PageContext';
import { useGetContentTranslations } from 'utils/useGetContentTranslations';
import { useIsomorphicLayoutEffect } from 'utils/useIsomorphicLayoutEffect';
import { getSectionTabs } from 'src/utils/getSectionTabs';
import { getSectionNavigationMode } from 'src/utils/getSectionNavigationMode';
import { useScrollSpy } from './useScrollSpy';
import { useTabQueryState } from './useTabQueryState';

const horizontalScrollStep = 50;

export function useTabNavigation() {
  const { page } = useContext(PageContext);
  const { translate, translateDefault } = useGetContentTranslations();
  const tabNavigationRef = useRef<HTMLDivElement>(null);
  const sectionNavigationMode = getSectionNavigationMode(page?.sections ?? []);
  const navigationMode = sectionNavigationMode === 'tabbed_content' ? 'tabs' : 'anchor';
  const { tabs: sectionTitles } = useMemo(
    () => getSectionTabs(page?.sections ?? [], translate, translateDefault),
    [page?.sections, translate, translateDefault],
  );
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(true);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [stickyTop, setStickyTop] = useState(0);
  const sectionIds = useMemo(() => sectionTitles.map((tab) => tab.slug), [sectionTitles]);
  const isTabsMode = navigationMode === 'tabs';
  const scrollSpy = useScrollSpy({ sectionIds, stickyTop, tabNavigationRef, enabled: !isTabsMode });
  const tabQueryState = useTabQueryState({ sectionIds, stickyTop });
  const { activeTab, onTabClick } = isTabsMode ? tabQueryState : scrollSpy;

  useIsomorphicLayoutEffect(() => {
    const updateStickyTop = () => {
      const headerHeight = document.querySelector('header')?.getBoundingClientRect().height || 0;
      setStickyTop(headerHeight);
    };
    updateStickyTop();
    window.addEventListener('resize', updateStickyTop);
    return () => {
      window.removeEventListener('resize', updateStickyTop);
    };
  }, []);

  useEffect(() => {
    const onResize = () => {
      const element = tabNavigationRef.current;
      if (element) {
        const { scrollWidth } = element;
        const { clientWidth } = element;
        if (scrollWidth > clientWidth) {
          setIsOverflowing(true);
        } else {
          setIsOverflowing(false);
        }
      }
    };
    onResize();
    const timeoutId = setTimeout(onResize, 100);
    window.addEventListener('resize', onResize);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', onResize);
    };
  }, [tabNavigationRef]);

  useEffect(() => {
    const element = tabNavigationRef.current;
    const onScroll = () => {
      if (element) {
        const { scrollWidth, clientWidth, scrollLeft } = element;
        setCanScrollLeft(scrollLeft > 0);
        setCanScrollRight(Math.round(scrollLeft + clientWidth) < scrollWidth);
      }
    };
    element?.addEventListener('scroll', onScroll);
    window.addEventListener('resize', onScroll);
    return () => {
      element?.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [tabNavigationRef]);

  const scrollLeft = () => {
    if (tabNavigationRef.current) {
      tabNavigationRef.current.scrollTo({
        left: tabNavigationRef.current.scrollLeft - horizontalScrollStep,
        behavior: 'smooth',
      });
    }
  };
  const scrollRight = () => {
    if (tabNavigationRef.current) {
      tabNavigationRef.current.scrollTo({
        left: tabNavigationRef.current.scrollLeft + horizontalScrollStep,
        behavior: 'smooth',
      });
    }
  };

  const scrollToSection = (id: string) => {
    const section = document.getElementById(id);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return {
    sectionTitles,
    navigationMode,
    sectionNavigationMode,
    activeTab,
    onTabClick,
    tabNavigationRef,
    isOverflowing,
    scrollLeft,
    scrollRight,
    scrollToSection,
    canScrollLeft,
    canScrollRight,
    stickyTop,
  };
}
